// @ts-nocheck
import { ThemeEvents } from '@theme/events';

/**
 * @param {number} cents
 * @param {string} currencyCode
 * @returns {string}
 */
function formatMoney(cents, currencyCode) {
  const amount = Number(cents) / 100;

  if (!Number.isFinite(amount)) return '';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {number}
 */
function getQuantity(form) {
  const quantityInput = form?.querySelector('input[name="quantity"]');
  const quantity = quantityInput ? Number(quantityInput.value) : 1;
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

/**
 * @param {HTMLElement} root
 */
function syncRoot(root) {
  if (!(root instanceof HTMLElement)) return;

  const container = root.closest('.shopify-section, dialog, product-card') || document;
  const form = container.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  const planInput = form.querySelector('input[data-subscription-selling-plan][name="selling_plan"]');
  const variantIdInput = form.querySelector('input[name="id"]');
  const button = form.querySelector('[ref="addToCartButton"]');
  if (!(planInput instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

  const pricingSlot = button.querySelector('[data-subscription-pricing]');
  const currentSlot = button.querySelector('[data-subscription-current]');
  const originalSlot = button.querySelector('[data-subscription-original]');
  const labelSlot = button.querySelector('.add-to-cart-text__label');
  const oneTimeButton = container.querySelector('[data-one-time-purchase-trigger]');
  const oneTimeButtonContainer = container.querySelector('[data-one-time-purchase-container]');
  const currencyCode = /** @type {HTMLElement} */ (form.closest('product-form-component'))?.dataset.currencyCode || 'USD';
  const hasSellingPlans = root.dataset.hasSellingPlans === 'true';
  const isOneTimeMode = root.dataset.purchaseMode === 'one-time';

  if (oneTimeButtonContainer instanceof HTMLElement) {
    oneTimeButtonContainer.hidden = !hasSellingPlans;
  }

  if (labelSlot instanceof HTMLElement && !labelSlot.dataset.defaultLabel) {
    labelSlot.dataset.defaultLabel = labelSlot.textContent?.trim() || 'Add to cart';
  }

  const selectedRadio = /** @type {HTMLInputElement | null} */ (
    root.querySelector('.product-subscription-selector__radio:checked')
  );
  const selectedAccordion = selectedRadio instanceof HTMLInputElement ? selectedRadio.closest('accordion-custom') : null;

  if (oneTimeButton instanceof HTMLButtonElement) {
    oneTimeButton.classList.toggle('is-active', isOneTimeMode);
    oneTimeButton.setAttribute('aria-pressed', isOneTimeMode ? 'true' : 'false');
  }

  if (!(selectedRadio instanceof HTMLInputElement) || !(selectedAccordion instanceof HTMLElement)) {
    planInput.value = '';
    if (pricingSlot instanceof HTMLElement) pricingSlot.hidden = true;
    if (currentSlot) currentSlot.textContent = '';
    if (originalSlot) originalSlot.textContent = '';

    if (labelSlot instanceof HTMLElement) {
      labelSlot.textContent = labelSlot.dataset.defaultLabel || 'Add to cart';
    }
    return;
  }

  const planId = selectedAccordion.dataset.sellingPlanId || '';
  const variantId = selectedAccordion.dataset.variantId || '';
  const sellingPlanPrice = Number(selectedAccordion.dataset.sellingPlanPrice || 0);
  const comparePrice = Number(selectedAccordion.dataset.sellingPlanComparePrice || 0);
  const quantity = getQuantity(form);

  const totalDiscounted = sellingPlanPrice * quantity;
  const totalOriginal = comparePrice * quantity;

  planInput.value = isOneTimeMode ? '' : planId;
  
  // For one-time mode, use the first variant ID instead of selected subscription variant
  const variantIdForSubmit = isOneTimeMode ? (root.dataset.firstVariantId || '') : variantId;
  if (variantIdInput instanceof HTMLInputElement && variantIdForSubmit) {
    variantIdInput.value = variantIdForSubmit;
  }

  if (labelSlot instanceof HTMLElement) {
    labelSlot.textContent = labelSlot.dataset.defaultLabel || 'Add to cart';
  }

  if (pricingSlot instanceof HTMLElement) pricingSlot.hidden = false;
  if (currentSlot instanceof HTMLElement) currentSlot.textContent = formatMoney(totalDiscounted, currencyCode);
  if (originalSlot instanceof HTMLElement) originalSlot.textContent = formatMoney(totalOriginal, currencyCode);
}

function syncAll() {
  for (const root of document.querySelectorAll('[data-subscription-accordion-group]')) {
    syncRoot(/** @type {HTMLElement} */ (root));
  }
}

function scheduleSync() {
  requestAnimationFrame(syncAll);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
} else {
  scheduleSync();
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.classList.contains('product-subscription-selector__radio')) return;

  const root = target.closest('[data-subscription-accordion-group]');
  if (root instanceof HTMLElement) {
    root.dataset.purchaseMode = 'subscription';
  }

  scheduleSync();
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-one-time-purchase-trigger]') : null;
  if (!(target instanceof HTMLButtonElement)) return;

  let root = target.closest('[data-subscription-accordion-group]');
  if (!(root instanceof HTMLElement)) {
    const container = target.closest('.shopify-section, dialog, product-card') || document;
    root = container.querySelector('[data-subscription-accordion-group]');
  }
  if (!(root instanceof HTMLElement)) return;

  root.dataset.purchaseMode = 'one-time';

  const form = target.form || target.closest('form');
  if (form instanceof HTMLFormElement) {
    const planInput = form.querySelector('input[data-subscription-selling-plan][name="selling_plan"]');
    if (planInput instanceof HTMLInputElement) {
      // Set immediately so this same submit click posts as one-time purchase.
      planInput.value = '';
    }
  }

  // CRITICAL: Sync immediately (not scheduled) before form submits
  syncAll();
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('button[ref="addToCartButton"]') : null;
  if (!(target instanceof HTMLButtonElement)) return;
  if (target.hasAttribute('data-one-time-purchase-trigger')) return;

  const container = target.closest('.shopify-section, dialog, product-card') || document;
  const root = container.querySelector('[data-subscription-accordion-group]');
  if (!(root instanceof HTMLElement)) return;

  // Sticky add-to-cart can explicitly choose one-time mode; do not override it here.
  if (root.dataset.stickyPurchaseMode === 'one-time') {
    root.dataset.purchaseMode = 'one-time';
    return;
  }

  // Ensure subscription add-to-cart always submits the currently selected plan/variant.
  root.dataset.purchaseMode = 'subscription';
  syncRoot(root);
});

document.addEventListener(ThemeEvents.variantUpdate, scheduleSync);
document.addEventListener(ThemeEvents.cartUpdate, scheduleSync);
